import { SCORED_ASES_QUESTION_IDS } from './proInstrument';
import type { AsesInstrumentConfig, AsesQuestion } from './proInstrument';
import { usesScaleInput, shouldRenderAsesForm } from './proInstrument';
import { WpLeadWidget } from '../contexts/lead';
import { WpSlideWidget } from '../contexts/pitch';
import { findLeadWidgetByWidgetId } from './proFollowUp';

export type AsesAnswers = Record<string, string | number>;

const ADL_QUESTION_IDS = SCORED_ASES_QUESTION_IDS.slice(1);

export function isQuestionAnswered(question: AsesQuestion, answers: AsesAnswers): boolean {
  const value = answers[question.id];
  if (value === undefined || value === '') {
    return false;
  }
  if (usesScaleInput(question)) {
    return !Number.isNaN(Number(value));
  }
  if (question.type === 'short') {
    return String(value).trim().length > 0;
  }
  if (question.type === 'yesno') {
    return value === 'Yes' || value === 'No';
  }
  if (question.type === 'choice') {
    return !Number.isNaN(Number(value));
  }
  return false;
}

export function canAdvanceFromQuestion(question: AsesQuestion, answers: AsesAnswers): boolean {
  return isQuestionAnswered(question, answers);
}

export function isScoredAnswerComplete(answers: AsesAnswers): boolean {
  const pain = answers.q7;
  if (pain === undefined || pain === '' || Number.isNaN(Number(pain))) {
    return false;
  }
  for (const id of ADL_QUESTION_IDS) {
    const value = answers[id];
    if (value === undefined || value === '' || Number.isNaN(Number(value))) {
      return false;
    }
  }
  return true;
}

export function areAllAsesQuestionsAnswered(
  instrumentConfig: AsesInstrumentConfig,
  values?: string[] | null,
): boolean {
  const answers = parseAsesAnswers(values);
  let questionCount = 0;

  for (const section of instrumentConfig.sections) {
    for (const question of section.questions) {
      questionCount += 1;
      if (!isQuestionAnswered(question, answers)) {
        return false;
      }
    }
  }

  return questionCount > 0;
}

/** Keep full answer map in lead state while the patient fills the form. */
export function buildAsesLeadValues(answers: AsesAnswers): string[] {
  return [JSON.stringify(answers)];
}

/** Format A for submitCampaignLandingPage / trackLead when scoring fields are complete. */
export function buildAsesApiValues(answers: AsesAnswers): string[] {
  if (!isScoredAnswerComplete(answers)) {
    return [JSON.stringify(answers)];
  }
  return SCORED_ASES_QUESTION_IDS.map((id) => String(answers[id]));
}

/** @deprecated Use buildAsesLeadValues while editing, buildAsesApiValues on submit. */
export function buildAsesSubmitValues(answers: AsesAnswers): string[] {
  return buildAsesLeadValues(answers);
}

export function areCampaignAsesWidgetsComplete(
  widgets: WpSlideWidget[],
  leadWidgets: WpLeadWidget[],
): boolean {
  const asesWidgets = widgets.filter((widget) => shouldRenderAsesForm(widget));
  if (!asesWidgets.length) {
    return true;
  }

  for (const widget of asesWidgets) {
    const instrumentConfig = widget.instrumentConfig;
    if (!instrumentConfig?.sections?.length) {
      return false;
    }
    const leadWidget = findLeadWidgetByWidgetId(leadWidgets, widget.id);
    if (!areAllAsesQuestionsAnswered(instrumentConfig, leadWidget?.values)) {
      return false;
    }
  }

  return true;
}

export function transformAsesWidgetValuesForSubmit(
  leadWidgets: WpLeadWidget[],
  campaignWidgets: WpSlideWidget[],
): WpLeadWidget[] {
  return leadWidgets.map((leadWidget) => {
    const campaignWidget = campaignWidgets.find((widget) => widget.id === leadWidget.widgetId);
    if (!campaignWidget || !shouldRenderAsesForm(campaignWidget)) {
      return leadWidget;
    }
    const answers = parseAsesAnswers(leadWidget.values);
    return {
      ...leadWidget,
      values: buildAsesApiValues(answers),
    };
  });
}

export function parseAsesAnswers(values?: string[] | null): AsesAnswers {
  if (!values?.length) {
    return {};
  }

  if (values.length === SCORED_ASES_QUESTION_IDS.length && values.every((value) => /^-?\d+$/.test(value))) {
    const answers: AsesAnswers = {};
    SCORED_ASES_QUESTION_IDS.forEach((id, index) => {
      answers[id] = parseInt(values[index], 10);
    });
    return answers;
  }

  const first = values[0];
  if (first?.startsWith('{')) {
    try {
      return JSON.parse(first) as AsesAnswers;
    } catch {
      return {};
    }
  }

  return {};
}
